import React, { useState, useEffect, useCallback } from 'react';
import { Mail, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const EmailHistory = ({ customerId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchHistory = useCallback(async () => {
    if (!customerId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin-emails/history/${customerId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Error fetching email history:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, customerId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#c9a55a]"></div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-6">
        <Mail className="w-10 h-10 text-[#f5f3ed]/30 mx-auto mb-2" />
        <p className="text-[#f5f3ed]/50 text-sm">No emails sent to this customer yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {history.map(email => (
        <div
          key={email.email_id}
          className="bg-[#1a1f1a]/50 rounded-lg overflow-hidden"
        >
          <button
            className="w-full px-4 py-3 flex items-start justify-between text-left"
            onClick={() => setExpandedId(expandedId === email.email_id ? null : email.email_id)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-[#c9a55a] flex-shrink-0" />
                <p className="text-[#f5f3ed] font-medium truncate">{email.subject}</p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-[#f5f3ed]/50">{formatDate(email.sent_at)}</span>
                {email.template_name && (
                  <span className="bg-[#c9a55a]/20 text-[#c9a55a] px-2 py-0.5 rounded">
                    {email.template_name}
                  </span>
                )}
                {email.attachments?.length > 0 && (
                  <span className="text-[#f5f3ed]/50 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {email.attachments.length}
                  </span>
                )}
              </div>
            </div>
            {expandedId === email.email_id ? (
              <ChevronUp className="w-5 h-5 text-[#f5f3ed]/50 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#f5f3ed]/50 flex-shrink-0" />
            )}
          </button>
          
          {expandedId === email.email_id && (
            <div className="px-4 pb-4 border-t border-[#2d5f3f]">
              <div 
                className="mt-3 p-4 bg-white rounded text-gray-800 text-sm prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: email.body }}
              />
              {email.attachments?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[#f5f3ed]/50 text-xs mb-1">Attachments:</p>
                  <div className="flex flex-wrap gap-2">
                    {email.attachments.map((name, i) => (
                      <span key={i} className="bg-[#2d5f3f]/50 text-[#f5f3ed]/70 text-xs px-2 py-1 rounded">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default EmailHistory;
